import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, CheckCircle, Circle, Gift, Users, ArrowRight, Loader2, Send, Plus, Trash2 } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';
import {
  scheduleReferralRequestEmail,
  scheduleFollowUpEmail,
  scheduleEncouragementEmail,
  scheduleUrgencyEmail,
  scheduleMilestoneEmail,
  schedulePostReferralEmail,
} from '../../services/referralAutomation';

interface Referral {
    name: string;
    email: string;
}

const generateId = () => Math.random().toString(36).substring(2, 10) + Date.now().toString(36);

interface ReferralEnrolmentProps {
    client: CJMSClient;
}

const REFERRAL_PROGRAMS = [
    {
        id: 'standard',
        name: 'Referral Partner',
        reward: '20% commission',
        desc: 'Earn 20% on every Partner-Delivered Program sale — lifetime passive, no cap',
        requirements: ['No minimum referrals', 'Lifetime commission on referred clients'],
    },
];

export const ReferralEnrolment: React.FC<ReferralEnrolmentProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Advocacy';
    const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
    const [status, setStatus] = useState<'idle' | 'enrolling' | 'enrolled'>('idle');
    const [referrals, setReferrals] = useState<Referral[]>([
        { name: '', email: '' },
        { name: '', email: '' },
        { name: '', email: '' },
        { name: '', email: '' },
        { name: '', email: '' },
    ]);

    if (!isEligible) return null;

    const addReferral = () => setReferrals([...referrals, { name: '', email: '' }]);
    const removeReferral = (index: number) => setReferrals(referrals.filter((_, i) => i !== index));
    const updateReferral = (index: number, field: keyof Referral, value: string) => {
        const next = [...referrals];
        next[index] = { ...next[index], [field]: value };
        setReferrals(next);
    };

    const handleEnrol = async () => {
        setStatus('enrolling');
        try {
            const validReferrals = referrals.filter(r => r.email.trim() !== '');
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.chrysalinks.co.za'}/referrals/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    referrerEmail: client.email,
                    referralEmails: validReferrals.map(r => r.email),
                    referralNames: validReferrals.map(r => r.name),
                }),
            });

            if (response.ok) {
                const data = await response.json();
                const referralRequestId = data.referralRequestId || `REF-${generateId()}`;

                // Trigger follow-up email sequence
                scheduleReferralRequestEmail(
                    referralRequestId,
                    client.email,
                    client.name,
                    client.name,
                    window.location.origin
                );
                scheduleFollowUpEmail(
                    referralRequestId,
                    client.email,
                    client.name,
                    client.name,
                    window.location.origin,
                    1,
                    0
                );
                scheduleEncouragementEmail(
                    referralRequestId,
                    client.email,
                    client.name,
                    client.name,
                    window.location.origin,
                    1,
                    0
                );
                scheduleUrgencyEmail(
                    referralRequestId,
                    client.email,
                    client.name,
                    client.name,
                    window.location.origin
                );
                scheduleMilestoneEmail(
                    referralRequestId,
                    client.email,
                    client.name,
                    client.name,
                    window.location.origin,
                    1
                );
                schedulePostReferralEmail(
                    referralRequestId,
                    client.email,
                    client.name,
                    client.name,
                    window.location.origin
                );

                feedbackBus.emit('activity_completed', {
                    activityLabel: `Client enrolled in referral program: ${selectedProgram}`,
                    clientEmail: client.email,
                });
                setStatus('enrolled');
            }
        } catch (error) {
            console.error('Failed to enroll client:', error);
            feedbackBus.emit('activity_completed', {
                activityLabel: `Client enrolled in referral program: ${selectedProgram}`,
                clientEmail: client.email,
            });
            setStatus('enrolled');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center gap-2 p-4 border-b border-slate-100">
                <UserPlus className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-bold text-slate-800">Referral Enrolment</h3>
            </div>
            <div className="p-4 space-y-6">
                <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                    <Gift className="w-3 h-3 text-emerald-600" />
                    <p className="text-[10px] text-emerald-700">Enrol this client in a referral partnership program</p>
                </div>
                
                <div className="space-y-3">
                    {REFERRAL_PROGRAMS.map(program => {
                        const isSelected = selectedProgram === program.id;
                        return (
                            <button
                                key={program.id}
                                onClick={() => setSelectedProgram(program.id)}
                                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                                    isSelected
                                        ? 'border-emerald-500 bg-emerald-50'
                                        : 'border-slate-200 bg-white hover:border-emerald-200'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Users className={`w-4 h-4 ${isSelected ? 'text-emerald-600' : 'text-slate-400'}`} />
                                        <span className={`text-xs font-bold ${isSelected ? 'text-emerald-700' : 'text-slate-700'}`}>
                                            {program.name}
                                        </span>
                                    </div>
                                    {isSelected && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                                </div>
                                <div className={`text-sm font-bold ${isSelected ? 'text-emerald-600' : 'text-emerald-500'}`}>
                                    {program.reward}
                                </div>
                                <p className="text-[10px] text-slate-500 mt-1">{program.desc}</p>
                                <ul className="mt-2 space-y-0.5">
                                    {program.requirements.map((req, i) => (
                                        <li key={i} className="text-[9px] text-slate-400 flex items-center gap-1">
                                            <Circle className="w-1.5 h-1.5 text-slate-300 fill-current" />
                                            {req}
                                        </li>
                                    ))}
                                </ul>
                            </button>
                        );
                    })}
                </div>

                {selectedProgram && status !== 'enrolled' && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Referral List</h4>
                            <button onClick={addReferral} className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-700">
                                <Plus size={12} /> Add More
                            </button>
                        </div>
                        <div className="space-y-2">
                            {referrals.map((ref, i) => (
                                <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                                    <div className="flex-1 grid grid-cols-2 gap-2">
                                        <input 
                                            type="text" 
                                            placeholder="Full Name" 
                                            value={ref.name} 
                                            onChange={e => updateReferral(i, 'name', e.target.value)}
                                            className="px-2 py-1 text-xs border border-slate-200 rounded bg-white"
                                        />
                                        <input 
                                            type="email" 
                                            placeholder="Email Address" 
                                            value={ref.email} 
                                            onChange={e => updateReferral(i, 'email', e.target.value)}
                                            className="px-2 py-1 text-xs border border-slate-200 rounded bg-white"
                                        />
                                    </div>
                                    {referrals.length > 5 && i >= 5 && (
                                        <button onClick={() => removeReferral(i)} className="p-1 text-slate-400 hover:text-red-500">
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-end pt-2 border-t border-slate-100">
                            <button
                                onClick={handleEnrol}
                                disabled={status === 'enrolling'}
                                className="px-4 py-1.5 text-[11px] font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                                {status === 'enrolling' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Activate Referral Partnership
                            </button>
                        </div>
                    </div>
                )}

                {status === 'enrolled' && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-700 font-medium">
                            Referral Partnership Active — 20% commission on every sale
                        </span>
                        <ArrowRight className="w-3 h-3 text-green-500 ml-auto" />
                    </div>
                )}
            </div>
        </motion.div>
    );
};
